import React, { useRef, useState } from 'react';
import { Box, Chip, Menu, MenuItem, InputLabel } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { PersonType } from 'API';
import PersonService from 'services/person';
import { FIRM_COMPANY } from 'services/firm';
import S3Avatar from './S3Avatar';

const { fullname, initials } = PersonService;

interface Props {
  canEdit?: boolean;
  chipColor?: 'default' | 'primary' | 'secondary';
  employer?: string;
  fullWidth?: boolean;
  id?: string;
  items: Array<Person>;
  label: string;
  noEmployerLabel?: boolean;
  onChange: (person: Person) => void;
  required?: boolean;
  selectedItem: Person;
  truncateName?: boolean;
}

const Dropdown: React.FC<Props> = ({
  canEdit,
  chipColor,
  employer,
  fullWidth,
  id,
  items,
  label,
  noEmployerLabel,
  onChange,
  required,
  selectedItem,
  truncateName,
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const iconRef = useRef<SVGSVGElement | null>(null);

  const handleMenuClick = () => {
    setIsOpen(!isOpen);
  };

  const handleMenuClose = () => {
    setIsOpen(false);
  };

  const handleChange = (person: Person) => {
    onChange(person);
    handleMenuClose();
  };

  return (
    <Box
      style={{ maxWidth: fullWidth ? '100%' : '200px' }}
      className="dropDownStyles"
      sx={{ paddingTop: '1rem', position: 'relative', width: '100%' }}
      onClick={canEdit ? handleMenuClick : undefined}
      id={id}
    >
      <InputLabel
        sx={Boolean(selectedItem) || isOpen
          ? { position: 'absolute', transform: 'translate(0, -1rem) scale(0.75)' }
          : { position: 'absolute', transform: 'translate(0, 0.5rem) scale(1)' }}
        required={required}
        shrink={Boolean(selectedItem) || isOpen}
      >
        {label}
      </InputLabel>
      <Box
        sx={(t) => ({
          alignItems: 'center',
          borderBottom: `2px solid ${t.palette.primary1}`,
          boxSizing: 'content-box',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          minHeight: '2.5rem',
          paddingBottom: t.spacing(0.5),
        })}
      >
        <Box
          sx={(t) => ({
            display: 'flex',
            flexWrap: 'wrap',
            width: 'calc(100% - 1.25rem)',
            zIndex: 10,
            '& > *': { margin: t.spacing(0.5) },
          })}
        >
          {selectedItem && (
            <Chip
              color={chipColor}
              label={(truncateName && fullname(selectedItem)?.length > 15)
                ? `${fullname(selectedItem).substr(0, 12)}...`
                : fullname(selectedItem)}
              avatar={(
                <S3Avatar picture={selectedItem.avatar} alt={fullname(selectedItem)}>
                  {initials(selectedItem)}
                </S3Avatar>
              )}
              sx={(t) => ({
                cursor: 'pointer',
                maxWidth: '100%',
                height: 30,
                fontWeight: 500,
                // Soft, on-brand primary chip: a faint orange-tinted pill with
                // an orange avatar (white initials) and orange text. Overrides
                // the theme's grey6 fill + white label, which were illegible.
                // (Default/secondary chips are unaffected.)
                ...(chipColor === 'primary' && {
                  backgroundColor: alpha(t.palette.primary1, 0.12),
                  color: t.palette.primary1,
                  '& .MuiChip-avatar, & .MuiChip-avatarColorPrimary': {
                    backgroundColor: t.palette.primary1,
                    color: '#fff',
                  },
                }),
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ...(chipColor === 'primary' && { color: t.palette.primary1 }),
                },
              })}
            />
          )}
        </Box>
        {canEdit && (
          <ExpandMoreIcon
            ref={iconRef}
            sx={isOpen ? { transform: 'rotate(180deg)' } : undefined}
            color="primary"
          />
        )}
        <Menu
          anchorEl={iconRef?.current}
          open={isOpen}
          onClose={handleMenuClick}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          keepMounted
        >
          {items.map((item: Person) => (
            <MenuItem
              sx={{ flexWrap: 'wrap' }}
              key={item.id}
              value={item.id}
              onClick={() => handleChange(item)}
              selected={item.id === selectedItem?.id}
            >
              {fullname(item)}
              {!noEmployerLabel && (
                <>
                  {item.type === PersonType.TeamMember
                    && (
                      <Box component="div" sx={{ fontSize: '0.875rem', width: '100%' }}>
                        {FIRM_COMPANY.name}
                      </Box>
                    )}
                  {item.type === PersonType.Employer
                    && employer && (
                    <Box component="div" sx={{ fontSize: '0.875rem', width: '100%' }}>
                      {employer}
                    </Box>
                  )}
                </>
              )}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </Box>
  );
};

export default Dropdown;
